/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store (one extra so we can show max_products after excluding current on PDP) */
  static #MAX_PRODUCTS = 5;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {string} [productHandle] - The handle of the product (optional, will be extracted from URL if not provided).
   */
  static addProduct(productId, productHandle = null) {
    // Extract handle from current URL if not provided and we're on a product page
    if (!productHandle && window.location.pathname.startsWith('/products/')) {
      const pathParts = window.location.pathname.split('/products/');
      if (pathParts.length > 1) {
        productHandle = pathParts[1].split('?')[0].split('#')[0];
      }
    }

    let viewedProducts = this.getProducts();

    // Remove existing entry with same ID
    viewedProducts = viewedProducts.filter(
      (/** @type {{id: string, handle?: string}} */ item) => {
        const id = typeof item === 'string' ? item : item.id;
        return id !== productId;
      }
    );

    // Add new product (store as object with id and handle if handle is available)
    const productData = productHandle
      ? { id: productId, handle: productHandle }
      : productId;
    viewedProducts.unshift(productData);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {Array<string | {id: string, handle: string}>} The list of viewed products (IDs or objects with id and handle).
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }

  /**
   * Gets product IDs from the stored products.
   * @returns {string[]} Array of product IDs.
   */
  static getProductIds() {
    const products = this.getProducts();
    return products.map((item) => {
      return typeof item === 'string' ? item : item.id;
    });
  }

  /**
   * Gets product handles from the stored products (if available).
   * @returns {string[]} Array of product handles (may contain null values).
   */
  static getProductHandles() {
    const products = this.getProducts();
    return products.map((item) => {
      return typeof item === 'string' ? null : item.handle || null;
    });
  }
}
